import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { LiteratureReference } from '@/shared/api/transcriptor/transcriptorApi';

import { dentalConsultationMessages } from '../../config';
import { getDoiUrl, getPubMedUrl } from '../../lib';

import styles from './LiteratureReferences.module.scss';

type LiteratureReferencesProps = {
  references: LiteratureReference[];
  className?: string;
};

const formatAuthors = (authors: string | null | undefined): string => {
  if (!authors) {
    return '';
  }

  const authorList = authors.split(',').map((a) => a.trim());
  if (authorList.length > 3) {
    return `${authorList[0]} et al`;
  }
  return authorList.join(', ');
};

export const LiteratureReferences: FC<LiteratureReferencesProps> = ({
  references,
  className,
}) => {
  if (!references || references.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(styles.node, className)}
      data-component="literature-references"
    >
      <div className={styles.separator} />
      <h4 className={cn('h5', styles.title)}>
        <FormattedMessage
          {...dentalConsultationMessages.literatureReferences}
        />
      </h4>
      <ol className={styles.list}>
        {references.map((ref) => (
          <li key={ref.index} className={styles.listItem}>
            <p className={cn('p2', styles.referenceText)}>
              {formatAuthors(ref.authors)}
              {ref.authors && '. '}
              {ref.title}
              {ref.journal && (
                <>
                  {' '}
                  <span className={styles.journal}>{ref.journal}</span>
                </>
              )}
              {ref.year && ` (${ref.year})`}
              {(ref.doi || ref.pmid) && '. '}
              {ref.doi && (
                <>
                  <a
                    href={getDoiUrl(ref.doi)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    DOI
                  </a>
                </>
              )}
              {ref.doi && ref.pmid && ' | '}
              {ref.pmid && (
                <a
                  href={getPubMedUrl(ref.pmid)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.link}
                >
                  PubMed
                </a>
              )}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
};
